import { useMutation } from '@apollo/client';
import axios from 'axios';
import { useCallback } from 'react';
import { slugify } from 'transliteration';

import type { ProfileDocumentType } from '@xing-com/crate-common-graphql-types';
import {
  ProfileDocumentVisibility,
  UploadApplication,
} from '@xing-com/crate-common-graphql-types';
import type { UploadRequestMutation } from '@xing-com/crate-jobs-graphql';
import {
  CreateProfileDocumentDocument,
  DeleteProfileDocumentDocument,
  UploadRequestDocument,
} from '@xing-com/crate-jobs-graphql';

const UPLOAD_URI = '/upload/file';

export type RequestedUpload = NonNullable<
  NonNullable<UploadRequestMutation['uploadRequest']>['success']
>;

export type FileServerUploadError = 'FILE_DUPLICATION' | 'FILE_NAME_TOO_LONG';

type UploadFileOptions = {
  signal: AbortSignal;
  profileDocument?: {
    profileId: string;
    documentType: ProfileDocumentType;
  };
  onUploadRequested?: (upload: RequestedUpload) => void;
  onUploadProgress?: (progress: number, upload: RequestedUpload) => void;
};

type FileServerUploadResult = {
  upload: RequestedUpload;
  status?: number;
  error?: FileServerUploadError;
  profileDocumentId?: string;
};

type UseFileServerUploadResult = {
  uploadFile: (
    file: File,
    options: UploadFileOptions
  ) => Promise<FileServerUploadResult>;
  deleteFile: (
    upload: Pick<RequestedUpload, 'id' | 'authToken'>
  ) => Promise<void>;
  deleteProfileDocument: (profileDocument: {
    profileId: string;
    documentId: string;
  }) => Promise<void>;
};

export const useFileServerUpload = (): UseFileServerUploadResult => {
  const [uploadRequest] = useMutation(UploadRequestDocument);
  const [createProfileDocument] = useMutation(CreateProfileDocumentDocument);
  const [profileDocumentDelete] = useMutation(DeleteProfileDocumentDocument);

  const deleteFile = useCallback(
    async ({
      id,
      authToken,
    }: Pick<RequestedUpload, 'id' | 'authToken'>): Promise<void> => {
      await axios.delete(`${UPLOAD_URI}/${id}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Tus-Resumable': '1.0.0',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
    },
    []
  );

  const deleteProfileDocument = useCallback(
    async ({
      profileId,
      documentId,
    }: {
      profileId: string;
      documentId: string;
    }) => {
      const { data } = await profileDocumentDelete({
        variables: {
          input: { profileId, documentId },
        },
      });

      if (
        data?.profileDocumentDelete?.__typename !==
          'ProfileDocumentDeleteSuccess' ||
        !data.profileDocumentDelete.success
      ) {
        throw new Error('Error deleting profile document');
      }
    },
    [profileDocumentDelete]
  );

  const uploadFile = useCallback(
    async (
      file: File,
      {
        signal,
        profileDocument,
        onUploadRequested,
        onUploadProgress,
      }: UploadFileOptions
    ): Promise<FileServerUploadResult> => {
      const { data } = await uploadRequest({
        variables: {
          input: {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            application: UploadApplication.Jobs,
          },
        },
      });
      const upload = data?.uploadRequest?.success;

      if (!upload) {
        throw new Error('Error requesting file upload');
      }

      if (signal.aborted) {
        return { upload };
      }

      onUploadRequested?.(upload);

      if (!upload.url) {
        throw new Error('Error requesting file upload URL');
      }

      const response = await axios.patch(upload.url, file, {
        signal,
        headers: {
          Authorization: `Bearer ${upload.authToken}`,
          'Upload-Offset': '0',
          'Tus-Resumable': '1.0.0',
          'Content-Type': 'application/offset+octet-stream',
          'Content-Disposition': `attachment; filename="${slugify(file.name)}"`,
          'Upload-Metadata': `content-type="${slugify(file.type)}"`,
        },
        onUploadProgress: onUploadProgress
          ? ({ loaded, total }) => {
              if (total !== undefined && !signal.aborted) {
                onUploadProgress(Math.floor((loaded / total) * 100), upload);
              }
            }
          : undefined,
        validateStatus: () => true,
      });

      const error = getFileServerUploadError(response.status);

      if (error) {
        return { upload, status: response.status, error };
      }

      if (response.status !== 204 || !profileDocument) {
        return { upload, status: response.status };
      }

      if (signal.aborted) {
        return { upload, status: response.status };
      }

      try {
        const { data: profileDocumentData } = await createProfileDocument({
          variables: {
            input: {
              profileId: profileDocument.profileId,
              title: file.name,
              filename: file.name,
              uploadId: upload.id,
              documentType: profileDocument.documentType,
              visibility:
                profileDocument.documentType === 'CV'
                  ? ProfileDocumentVisibility.Recruiters
                  : ProfileDocumentVisibility.Nobody,
              consumer: 'jobsapply',
            },
          },
        });
        const createdProfileDocument =
          profileDocumentData?.profileDocumentCreate;

        if (
          createdProfileDocument?.__typename === 'ProfileDocumentMutationError'
        ) {
          const uploadError = getProfileDocumentCreationResultError({
            errors: createdProfileDocument.errors,
            message: createdProfileDocument.message,
          });

          if (uploadError) {
            try {
              await deleteFile(upload);
            } catch {
              // Preserve the profile-document creation error.
            }

            return { upload, status: response.status, error: uploadError };
          }

          throw new Error('Error creating profile document');
        }

        if (createdProfileDocument?.__typename !== 'ProfileDocument') {
          throw new Error('Error creating profile document');
        }

        if (signal.aborted) {
          try {
            await deleteProfileDocument({
              profileId: profileDocument.profileId,
              documentId: createdProfileDocument.id,
            });
          } catch {
            // Cancellation must not surface an error to the user.
          }

          return { upload, status: response.status };
        }

        return {
          upload,
          status: response.status,
          profileDocumentId: createdProfileDocument.id,
        };
      } catch (error) {
        try {
          await deleteFile(upload);
        } catch {
          // Preserve the profile-document creation error.
        }

        const uploadError = getProfileDocumentCreationError(error);

        if (uploadError) {
          return { upload, status: response.status, error: uploadError };
        }

        throw error;
      }
    },
    [createProfileDocument, deleteFile, deleteProfileDocument, uploadRequest]
  );

  return { uploadFile, deleteFile, deleteProfileDocument };
};

export const getFileServerUploadError = (
  status: number
): FileServerUploadError | undefined => {
  switch (status) {
    case 409:
      return 'FILE_DUPLICATION';
    case 422:
      return 'FILE_NAME_TOO_LONG';
    default:
      return undefined;
  }
};

export const getProfileDocumentCreationError = (
  error: unknown
): FileServerUploadError | undefined => {
  if (!isRecord(error) || !Array.isArray(error.graphQLErrors)) {
    return undefined;
  }

  return error.graphQLErrors.some(
    (graphQLError) =>
      isRecord(graphQLError) &&
      isRecord(graphQLError.details) &&
      graphQLError.details.message === 'DUPLICATE_DOCUMENT'
  )
    ? 'FILE_DUPLICATION'
    : undefined;
};

export const getProfileDocumentCreationResultError = ({
  errors,
  message,
}: {
  errors?: unknown;
  message?: string | null;
}): FileServerUploadError | undefined => {
  if (message === 'DUPLICATE_DOCUMENT') {
    return 'FILE_DUPLICATION';
  }

  if (!isRecord(errors)) {
    return undefined;
  }

  const fileNameErrors = [errors.title, errors.filename].flatMap((error) =>
    Array.isArray(error) ? error : []
  );

  if (fileNameErrors.includes('TOO_LONG')) {
    return 'FILE_NAME_TOO_LONG';
  }

  return undefined;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;
